<script>
  import { PayrollResource, ClientResource, ProjectResource } from '@sigmacloud/sigma-client/dist/resources'
  import Autocomplete from '@/components/SigmaClientNew/Autocomplete.vue'
  import DateField from '@/components/InputComponents/DateField.vue'
  import PayrollStatusList from '@/components/SigmaClientNew/PayrollStatusList.vue'
  import { getSimpleDate } from '@/utils/helpers/FormatDate'
  import { sliceIntoChunks } from '@/utils/helpers/ObjectSorting'

  export default {
    name: 'PayrollReports',
    components: {
      Autocomplete,
      DateField,
      PayrollStatusList
    },
    data () {
      return {
        iteration: 0,
        payrollRowData: undefined,
        reportOptions: {
          transactionStatuses: [ 'posted' ],
          accountQuickPick: 'all',
          type: null,
          dates: 'datesAll',
          sort: 'account',
          status: 'all',
          format: 'pdf',
          currencyFactor: undefined,
          report_currency_id: 1
        },
        selectedCodes: {
          period_start: undefined,
          period_end: undefined,
          time_posted_start: undefined,
          time_posted_end: undefined,
          effective_date_start: undefined,
          effective_date_end: undefined,
          pay_period_start_date: undefined,
          pay_period_end_date: undefined,
          client_id: undefined,
          project_id: undefined,
          account_id: [],
          company_id: undefined,
          transaction_id: undefined,
          employee_id: undefined,
          doc_number: undefined,
          bank_id: undefined,
          currency_id: []
        },
        transactionTypes: {     // Types of transactions and whether we need them or not
          'ckreg': { name: 'CKREG', include: true }
        },
        disableReportRun: false, // Disable run report button if false
        prettyJson: undefined,
        showJson: false,
        loading: false,
        recentPayrollRowData: undefined
      }
    },

    computed: {
      resources () {
        return {
          PayrollResource: PayrollResource,
          ClientResource: ClientResource,
          ProjectResource: ProjectResource
        }
      },
      width () {
        return '100%'
      },
      height () {
        return window.innerHeight * 0.9 + 'px'
      }
    },
    methods: {
      async getRecentPayrolls () {
        // Show status of recent payrolls (posted in the last week ending and open, draft, or audited)
        const today = new Date()
        const lastWeekEnding = getSimpleDate(today.setDate(today.getDate() - today.getDay() - 1), 'yyyy-mm-dd')

        let postedPayrollResources = await this.resources.PayrollResource.all({ query: { status: 'POSTED', time_posted__gte: this.selectedCodes.time_posted_end || lastWeekEnding } })
        let auditedPayrollResources = await this.resources.PayrollResource.all({ query: { status: 'AUDITED' } })
        let openPayrollResources = await this.resources.PayrollResource.all({ query: { status: 'OPEN', check_date__gte: lastWeekEnding } })
        let draftPayrollResources = await this.resources.PayrollResource.all({ query: { status: 'DRAFT', check_date__gte: lastWeekEnding } })
        let allPayrollResources = auditedPayrollResources.concat(openPayrollResources).concat(draftPayrollResources).concat(postedPayrollResources)
        // const projectIds = [ ...new Set(postedPayrollResources.map((res) => res.attributes.project).concat(auditedPayrollResources.map((res) => res.attributes.project)).concat(openPayrollResources.map((res) => res.attributes.project)).concat(draftPayrollResources.map((res) => res.attributes.project))) ]
        const projectIds = [ ...new Set(allPayrollResources.map((res) => res.attributes.project)) ]
        let clientIds
        let projectResources

        // There may be too many resources to load at once because of header size so split it to requests with 50 ids
        if (projectIds) {
          const splitSize = 50
          const projectSets = sliceIntoChunks(projectIds, splitSize)
          for (const i in projectSets) {
            await this.resources.ProjectResource.all({ query: { id__in: projectSets[i].join(',') } })
          }
          projectResources = this.resources.ProjectResource.getCachedAll().map((res) => res.resource)
          clientIds = projectResources.map((res) => res.attributes.client)
        }

        if (clientIds) {
          const splitSize = 20
          const clientSets = sliceIntoChunks(clientIds, splitSize)
          for (const i in clientSets) {
            await this.resources.ClientResource.all({ query: { id__in: clientSets[i].join(',') } })
          }
        }

        for (const i in projectResources) {
          await projectResources[i].resolveRelated({ managers: [ 'client' ] })
        }

        for (const i in allPayrollResources) {
          await allPayrollResources[i].resolveRelated({ managers: [ 'project' ] })
        }

        this.payrollRowData = allPayrollResources // TEMP
        this.iteration++
      },

      async refresh () {
        this.payrollRowData = undefined
        await this.getRecentPayrolls()
      }
    },

    async created () {
      await this.refresh()
    }
  }
</script>

<template>
  <div style="width: 100%;" v-loading.body="loading">
    <el-row type="flex" align="middle" justify="end" class="headerRow">
      <el-col>
        <el-row type="flex" align="middle" justify="start" style="font-size: 1.5em;">
          PAYROLL STATUS
        </el-row>
      </el-col>
      <el-col class="small">
          <a @click="showJson = ! showJson">.&nbsp;</a>
      </el-col>
      <el-col>
        <el-row type="flex" align="middle" justify="end">
          <el-tag v-if="disableReportRun" type="primary">
            <i class="el-icon-loading" style="margin: 0 6px;" />Running
          </el-tag>
          <el-button type="warning" plain @click="refresh" :disabled="disableReportRun">Refresh</el-button>
          <el-button v-if="registerRowData && registerRowData.length" type="success" @click="onGenerateExport" :disabled="disableReportRun" style="margin: 0 15px;">Save</el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" align="middle" justify="start" class="headerRow">
      <el-row type="flex" align="middle" justify="end">
        <el-col :span="6">Posting Date Start</el-col>
        <el-col :span="6">
          <DateField v-model="selectedCodes.time_posted_start" />
        </el-col>
        <el-col :span="6">Posting Date End</el-col>
        <el-col :span="6">
          <DateField v-model="selectedCodes.time_posted_end" />
        </el-col>
      </el-row>
    </el-row>
    <el-row>
      <el-col>
        <payroll-status-list
          name="payroll_status_list"
          ref="payroll_status_list"
          :key="iteration"
          :width="width"
          :height="height"
          :row-data="payrollRowData"
        />
      </el-col>
    </el-row>

  </div>
</template>

<style scoped>
  .small { font-size: 8px; }
  .right { float: right; text-align: right; padding: 0px 5px; }
  .left { float: left; text-align: left; padding: 0px 5px; }
  .selectRow { padding: 0px 20px; }
  .section { padding: 40px 20px 0px;  }
  .headerRow { padding: 15px 20px 0px; }
  .m-top-20 { margin-top: 20px; }
  .m-right-20 { margin-right: 20px; }
</style>
<style>
  .el-select { display: block; }
  .el-transfer-panel { width:36%; max-width: 350px }
  .el-transfer-panel__body { min-height: 400px }
  .el-transfer-panel__list { min-height: 360px }
  /* border: 1px solid #d1dbe5; box-shadow: 0 2px 4px rgba(0, 0, 0, .12),0 0 6px rgba(0, 0, 0, .04); display: inline-block; width: 30%; position: relative; height: 500px; */
</style>



// WEBPACK FOOTER //
// src/components/Payroll/PayrollStatus.vue